import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import { ok, err, type ApiResponse } from './_base';

// ═══════════════════════════════════════════════════════════════════════════
// Contactos de cobranza — log de gestiones con administraciones
// ═══════════════════════════════════════════════════════════════════════════

export const CANALES_CONTACTO = [
  { value: 'email', label: 'Email', icono: '✉️' },
  { value: 'whatsapp', label: 'WhatsApp', icono: '💬' },
  { value: 'llamada', label: 'Llamada', icono: '📞' },
  { value: 'visita', label: 'Visita', icono: '🚪' },
  { value: 'otro', label: 'Otro', icono: '📝' },
] as const;

export const RESULTADOS_CONTACTO = [
  { value: 'pendiente', label: 'No atendió / pendiente', tone: 'muted' as const },
  { value: 'recibido', label: 'Recibido sin compromiso', tone: 'info' as const },
  { value: 'prometio_pagar', label: 'Prometió pagar', tone: 'success' as const },
  { value: 'rechazo', label: 'Rechazó', tone: 'warn' as const },
  { value: 'escalo_intimacion', label: 'Escalado a intimación', tone: 'warn' as const },
  { value: 'escalo_judicial', label: 'Escalado a judicial', tone: 'danger' as const },
] as const;

export type CanalContacto = (typeof CANALES_CONTACTO)[number]['value'];
export type ResultadoContacto = (typeof RESULTADOS_CONTACTO)[number]['value'];

export interface ContactoCobranzaRow {
  id: string;
  fecha: string;
  canal: CanalContacto;
  resultado: ResultadoContacto;
  fecha_compromiso: string | null;
  nota: string | null;
  edificio_id: string | null;
  edificio_nombre: string | null;
  edificio_codigo: string | null;
  autor_id: string | null;
  autor_nombre: string | null;
}

export async function listContactosCobranza(
  administracionId: string,
  limit = 50,
): Promise<ApiResponse<ContactoCobranzaRow[]>> {
  const { data, error } = await supabase.rpc('list_contactos_cobranza', {
    p_administracion_id: administracionId,
    p_limite: limit,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok((data ?? []) as ContactoCobranzaRow[]);
}

export interface RegistrarContactoParams {
  administracionId: string;
  canal: CanalContacto;
  resultado: ResultadoContacto;
  edificioId?: string | null;
  fecha?: string; // ISO timestamptz; default: now() en server
  fechaCompromiso?: string | null;
  nota?: string;
  /** 0140 · tipo de plantilla usado si canal=email (R1, R2, etc). */
  tipoPlantilla?: string | null;
  /** 0140 · snapshot del total pendiente al momento del envío. */
  montoPendienteAlEnvio?: number | null;
}

export async function registrarContactoCobranza(
  params: RegistrarContactoParams,
): Promise<ApiResponse<string>> {
  const { data, error } = await supabase.rpc('registrar_contacto_cobranza', {
    p_administracion_id: params.administracionId,
    p_canal: params.canal,
    p_resultado: params.resultado,
    p_edificio_id: params.edificioId ?? undefined,
    p_fecha: params.fecha ?? undefined,
    p_fecha_compromiso: params.fechaCompromiso ?? undefined,
    p_nota: params.nota ?? undefined,
    p_tipo_plantilla: params.tipoPlantilla ?? undefined,
    p_monto_pendiente_al_envio: params.montoPendienteAlEnvio ?? undefined,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(String(data));
}
